import React, { useMemo, useState } from 'react'

import { artistKey, eventDayOptions, formatDayLabel, toDayKey } from '../../utils/scheduleUtils'

/**
 * Put artists on days without leaving the event page.
 *
 * Tap artists, tap a day. Deliberately not drag-and-drop: HTML5 drag events do
 * not fire on touch screens, and this app is used on a phone at a festival. Tap
 * also handles the bulk case better — selecting fifteen artists and assigning
 * them at once is one gesture per artist either way, but with dragging it is
 * fifteen separate drags.
 *
 * The day column stays collapsed until asked for, because for most events the
 * days are already known and this panel is not the point of the page.
 */
const DayAssigner = ({ event, artists, sharedDays, onAssign, onDone }) => {
  const [selected, setSelected] = useState(() => new Set())
  const [expanded, setExpanded] = useState(true)
  const [saving, setSaving] = useState(false)
  const [justSaved, setJustSaved] = useState('')

  const days = useMemo(() => eventDayOptions(event), [event])

  // What day each artist currently sits on, personal value first then shared —
  // the same precedence the day sections use, so the panel never disagrees with
  // the list right next to it.
  const currentDay = (artist) =>
    toDayKey(artist?.performanceDate) || sharedDays?.get(artistKey(artist?.name)) || ''

  const toggle = (index) => {
    setSelected(current => {
      const next = new Set(current)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  const assign = async (dayKey) => {
    if (selected.size === 0 || saving) return

    setSaving(true)
    const ok = await onAssign(Array.from(selected), dayKey)
    setSaving(false)

    if (ok) {
      setJustSaved(dayKey ? `Moved to ${formatDayLabel(dayKey)}` : 'Day cleared')
      setSelected(new Set())
      setTimeout(() => setJustSaved(''), 4000)
    }
  }

  const countOn = (dayKey) => artists.filter(artist => currentDay(artist) === dayKey).length

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-4 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h4 className="text-white font-semibold">Assign days</h4>
          <p className="text-xs text-gray-400">
            Pick artists, then choose a day. This sets the day on the shared
            lineup, so it updates for everyone at this event.
          </p>
        </div>
        <button
          type="button"
          onClick={onDone}
          className="text-sm bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg transition-colors"
        >
          Done
        </button>
      </div>

      {days.length === 0 && (
        <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
          This event has no dates yet, so there are no days to assign. Add a
          start and end date to the event first.
        </p>
      )}

      {days.length > 0 && (
        <div className="flex flex-col lg:flex-row gap-4">
          {/* The lineup, as a selectable list. Plain rows rather than the usual
              artist bubbles: those already own the tap gesture for marking an
              artist seen, and overloading it would make one tap mean two
              things depending on a mode the user cannot see. */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-300">
                {selected.size > 0 ? `${selected.size} selected` : 'Select artists'}
              </span>
              {selected.size > 0 && (
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  className="text-xs text-gray-400 hover:text-white transition-colors"
                >
                  Clear
                </button>
              )}
            </div>

            <div className="max-h-80 overflow-y-auto pr-1 space-y-1">
              {artists.map((artist, index) => {
                const isSelected = selected.has(index)
                const day = currentDay(artist)

                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => toggle(index)}
                    aria-pressed={isSelected}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition-colors flex items-center justify-between gap-3 ${
                      isSelected
                        ? 'bg-purple-500/25 border-purple-400/60 text-white'
                        : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    <span className="truncate">{artist.name}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {day ? formatDayLabel(day) : 'No day'}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* The day column. Collapsed to a single button on small screens so
              the lineup keeps the width; open by default on desktop. */}
          <div className="lg:w-56 flex-shrink-0">
            <button
              type="button"
              onClick={() => setExpanded(value => !value)}
              className="w-full flex items-center justify-between text-sm text-gray-300 hover:text-white px-3 py-2 rounded-lg bg-white/5 border border-white/10 transition-colors lg:cursor-default"
            >
              <span>Days ({days.length})</span>
              <span className="lg:hidden">{expanded ? '▾' : '▸'}</span>
            </button>

            {expanded && (
              <div className="mt-2 space-y-1">
                {days.map((dayKey, position) => (
                  <button
                    key={dayKey}
                    type="button"
                    disabled={selected.size === 0 || saving}
                    onClick={() => assign(dayKey)}
                    className="w-full text-left px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-purple-500/20 hover:border-purple-400/50 disabled:opacity-40 disabled:hover:bg-white/5 disabled:hover:border-white/10 disabled:cursor-not-allowed transition-colors"
                  >
                    <div className="text-white text-sm">Day {position + 1}</div>
                    <div className="text-xs text-gray-400">
                      {formatDayLabel(dayKey)} · {countOn(dayKey)}
                    </div>
                  </button>
                ))}

                <button
                  type="button"
                  disabled={selected.size === 0 || saving}
                  onClick={() => assign('')}
                  className="w-full text-left px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs text-gray-300"
                >
                  Clear day
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {(saving || justSaved) && (
        <p className="text-sm text-gray-300" role="status">
          {saving ? 'Saving…' : justSaved}
        </p>
      )}
    </div>
  )
}

export default DayAssigner
